import React, { useEffect, useState } from 'react';
import { GameState, SessionProgress, SpellingGameState, Lesson } from '../domain/types';
import StarMeter from '../components/StarMeter';
import './RewardScreen.css';

interface RewardScreenProps {
  gameState: GameState;
  spellingGameState: SpellingGameState;
  currentLesson: Lesson;
  lastSession: SessionProgress | null;
  onRestart: () => void;
  onGoHome: () => void;
  totalStars: number;
  onNextLesson: () => void;
}

const RewardScreen: React.FC<RewardScreenProps> = ({
  gameState,
  spellingGameState,
  currentLesson,
  lastSession,
  onRestart,
  onGoHome,
  totalStars,
  onNextLesson
}) => {
  const [showParticles, setShowParticles] = useState(false);

  useEffect(() => {
    // Mostrar partículas después de un breve delay
    const timer = setTimeout(() => {
      setShowParticles(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const isSpellingGame = currentLesson?.mode === 'spelling';
  const stars = isSpellingGame ? (spellingGameState.isCompleted ? 1 : 0) : gameState.stars;
  const totalChallenges = isSpellingGame ? 1 : gameState.challenges.length;
  const percentage = Math.round((stars / totalChallenges) * 100);

  const getEncouragementMessage = () => {
    if (isSpellingGame) {
      if (spellingGameState.isCompleted) {
        return "¡Excelente, Cata! Has aprendido a escribir tu nombre! ✨👑";
      } else {
        return "¡Sigue practicando, Cata! Puedes aprender a escribir tu nombre! 🌟";
      }
    } else {
      if (percentage >= 80) {
        return "¡Increíble, Princesa Cata! Eres una experta en sumas mágicas! 🌟";
      } else if (percentage >= 60) {
        return "¡Muy bien, Princesa Cata! Tu corona brilla con estrellitas! ✨";
      } else if (percentage >= 40) {
        return "¡Bien hecho, Princesa Cata! Sigue practicando para brillar más! 🌟";
      } else {
        return "¡Sigue intentando, Princesa Cata! Cada estrella cuenta! ✨";
      }
    }
  };

  return (
    <div className="reward-screen">
      <div className="reward-container">
        {/* Partículas de fondo */}
        {showParticles && (
          <div className="particles">
            {Array.from({ length: 20 }, (_, i) => (
              <div
                key={i}
                className="particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${2 + Math.random() * 2}s`
                }}
              >
                ✨
              </div>
            ))}
          </div>
        )}

        {/* Corona principal */}
        <div className="crown-section">
          <div className="crown">
            👑
          </div>
          <img 
            src="/assets/Finish.png" 
            alt="Cata completando" 
            className="finish-image"
          />
          <h1 className="reward-title">
            ¡Corona mágica completada! 👑✨
          </h1>
          <p className="reward-subtitle">
            {getEncouragementMessage()}
          </p>
        </div>

        {/* Estrellas ganadas */}
        <div className="stars-section">
          <StarMeter 
            current={stars} 
            total={totalChallenges}
            showLabel={true}
          />
          <div className="score-details">
            <div className="score-item">
              <span className="score-label">Estrellas ganadas:</span>
              <span className="score-value">{stars}/{totalChallenges}</span>
            </div>
            <div className="score-item">
              <span className="score-label">Porcentaje:</span>
              <span className="score-value">{percentage}%</span>
            </div>
          </div>
        </div>

        {/* Última sesión si existe */}
        {lastSession && (
          <div className="last-session">
            <h3>Última sesión:</h3>
            <p>{lastSession.stars} estrellas de {lastSession.total}</p>
          </div>
        )}

        {/* Botones de acción */}
        <div className="action-buttons">
          <button 
            className="action-button primary"
            onClick={onRestart}
          >
            {isSpellingGame ? 'Intentar otra vez' : 'Jugar otra vez'}
          </button>
          <button 
            className="action-button secondary"
            onClick={onNextLesson}
          >
            📚 Siguiente lección
          </button>
          <button 
            className="action-button tertiary"
            onClick={onGoHome}
          >
            🏠 Inicio
          </button>
        </div>
      </div>
    </div>
  );
};

export default RewardScreen;
